import { QueryClient } from '@tanstack/react-query'
import { createRouter, type RouterHistory } from '@tanstack/react-router'
import { ApiError } from './lib/api/client'
import { routeTree } from './routeTree.gen'

function isClientError(err: unknown): boolean {
  const status = (err as { status?: unknown } | null)?.status
  return typeof status === 'number' && status >= 400 && status < 500
}

export function createQueryClient() {
  const client: QueryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        // A 4xx will not change on retry (401 signs out, 403 is the role, 404 is gone, 501 is an unbuilt slice). The
        // case-view client throws its own ApiError class, so any error carrying an HTTP status counts.
        retry: (count, err) => !(err instanceof ApiError && err.status < 500) && !isClientError(err) && count < 1,
        // Cold open from cache: keep showing what we have while revalidating.
        gcTime: 12 * 60 * 60 * 1000,
      },
    },
  })
  return client
}

export function createAppRouter(queryClient: QueryClient, history?: RouterHistory) {
  return createRouter({
    routeTree,
    history,
    context: { queryClient },
    defaultPreload: 'intent',
    // Query owns caching; the router always asks it.
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
  })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>
  }
}
